import { apiFetch } from '../../lib/api';

const DEVELOPER_MANAGEMENT_FAMILY = 'fam-devmgmt';

export interface DeveloperOrgRef {
  org_id: string;
  name: string;
  slug: string;
  state: string;
  owner_user_id?: string;
  default_family_id?: string;
  // 本源家族:org 現行生效池(default_family_id)為共用平台池時,記錄其自有池。
  // 加入平台池=可逆疊加;停止加入即回到本源(08 §2.1.1)。
  source_family_id?: string;
  created_at?: number;
  updated_at?: number;
}

export interface Family {
  family_id: string;
  name: string;
  owner_scope: string;
  state: string;
  created_at?: number;
  updated_at?: number;
}

export interface ClientApp {
  client_id: string;
  status: string;
  family_id?: string;
  /** eco-* attribution (common#24/#79). The ecosystem a user registering through
   *  this client inherits as their base_ecosystem_id. Operator-settable (restful#84). */
  ecosystem_id?: string;
  capabilities?: string[];
  force_upgrade_below?: string;
  min_supported_version?: string;
  deprecated_at?: number;
  previous_key_expires_at?: number;
  created_at?: number;
  updated_at?: number;
}

export interface CustomerFamilyOverview {
  families: Family[];
  client_apps: ClientApp[];
  customer_orgs: DeveloperOrgRef[];
}

export type FamilyMode = 'platform_family' | 'owned_family';

export interface AssignCustomerFamilyInput {
  customer_ref: string;
  family_mode: FamilyMode;
  platform_family_id?: string;
  owned_family_id?: string;
  family_name?: string;
  client_id: string;
  app_key_hash: string;
  capabilities?: string[];
  reason: string;
  case_id: string;
}

export interface AssignCustomerFamilyResult {
  customer_ref: string;
  family_mode: FamilyMode;
  family: Family;
  client_app: ClientApp;
}

export interface MigrationCheck {
  code: string;
  status: string;
  detail: string;
}

export interface MigrationPreflightResult {
  client_id: string;
  current_family_id: string;
  target_family_id: string;
  migration_required: boolean;
  can_apply_inline: boolean;
  checks: MigrationCheck[];
}

export async function listCustomerFamilies(params: { orgId?: string; familyId?: string; limit?: number } = {}): Promise<CustomerFamilyOverview> {
  const res = await apiFetch<CustomerFamilyOverview>('/v3/admin/customer-families', {
    query: { org_id: params.orgId, family_id: params.familyId, limit: params.limit ?? 100 },
  });
  const families = Array.isArray(res.data?.families) ? res.data.families : [];
  const clientApps = Array.isArray(res.data?.client_apps) ? res.data.client_apps : [];
  return {
    families: families.filter((family) => family.family_id !== DEVELOPER_MANAGEMENT_FAMILY),
    client_apps: clientApps.filter((app) => app.family_id !== DEVELOPER_MANAGEMENT_FAMILY),
    customer_orgs: Array.isArray(res.data?.customer_orgs) ? res.data.customer_orgs : [],
  };
}

export async function assignCustomerFamily(input: AssignCustomerFamilyInput): Promise<AssignCustomerFamilyResult> {
  const res = await apiFetch<AssignCustomerFamilyResult>('/v3/admin/customer-families/assign', {
    method: 'POST',
    body: input,
  });
  return res.data;
}

export async function preflightCustomerFamilyMigration(input: {
  client_id: string;
  target_family_id: string;
  reason: string;
  case_id: string;
}): Promise<MigrationPreflightResult> {
  const res = await apiFetch<MigrationPreflightResult>('/v3/admin/customer-families/migration-preflight', {
    method: 'POST',
    body: input,
  });
  return {
    ...res.data,
    checks: Array.isArray(res.data?.checks) ? res.data.checks : [],
  };
}

export async function suspendCustomerFamily(familyId: string, body: { reason: string; case_id: string }): Promise<Family> {
  const res = await apiFetch<Family>(`/v3/admin/customer-families/${encodeURIComponent(familyId)}/suspend`, {
    method: 'POST',
    body,
  });
  return res.data;
}

// ---- client app 生命週期(08 §2.1.3,restful!163)----

export async function rotateClientAppKey(
  clientId: string,
  body: { new_app_key_hash: string; previous_key_window_days?: number; reason: string; case_id: string }
): Promise<ClientApp> {
  const res = await apiFetch<ClientApp>(`/v3/admin/client-apps/${encodeURIComponent(clientId)}/rotate-key`, {
    method: 'POST',
    body,
  });
  return res.data;
}

export async function revokeClientApp(clientId: string, body: { reason: string; case_id: string }): Promise<ClientApp> {
  const res = await apiFetch<ClientApp>(`/v3/admin/client-apps/${encodeURIComponent(clientId)}/revoke`, {
    method: 'POST',
    body,
  });
  return res.data;
}

export async function reactivateClientApp(clientId: string, body: { reason: string; case_id: string }): Promise<ClientApp> {
  const res = await apiFetch<ClientApp>(`/v3/admin/client-apps/${encodeURIComponent(clientId)}/reactivate`, {
    method: 'POST',
    body,
  });
  return res.data;
}

export async function setClientAppVersionPolicy(
  clientId: string,
  body: { min_supported_version: string; force_upgrade_below: string; deprecated_at?: number; reason: string; case_id: string }
): Promise<ClientApp> {
  const res = await apiFetch<ClientApp>(`/v3/admin/client-apps/${encodeURIComponent(clientId)}/version-policy`, {
    method: 'PUT',
    body,
  });
  return res.data;
}

// PUT /v3/admin/client-apps/{id}/ecosystem — set/clear client_apps.ecosystem_id
// (operator override, restful#84). Empty ecosystem_id clears the attribution.
export async function setClientAppEcosystem(
  clientId: string,
  body: { ecosystem_id: string; reason: string; case_id: string }
): Promise<ClientApp> {
  const res = await apiFetch<ClientApp>(`/v3/admin/client-apps/${encodeURIComponent(clientId)}/ecosystem`, {
    method: 'PUT',
    body,
  });
  return res.data;
}

// ---- app_key 產生(瀏覽器端;後端永遠只收 hash)----

// generateAppKey 產 32-byte 隨機 key(base64url 原文,一次性揭示後即丟)。
export function generateAppKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

// hashAppKey = base64url(SHA-256(app_key)) — 與後端 validAppKeyHash / gate hashKey 同式。
export async function hashAppKey(appKey: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(appKey));
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
